import React, { useContext, useEffect, useReducer } from 'react';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

// Services
import { useAuthContext } from '../auth.provider';
import { getUser } from '../../services/user.service';
import { initialState, userReducer } from './user.reducer';
import { isInitialized, setLoading, setResolvedUid, setUser } from './user.actions';
import { identifyUser } from './user.utils';
import { captureException } from '../../analytics/sentry';

export const UserContext = React.createContext(null);
export const UserDispatchContext = React.createContext(null);

export const UserProvider = ({ children }) => {
  const { firebaseUser, initialized: authInitialized, loading: authLoading } = useAuthContext();
  const [state, dispatch] = useReducer(userReducer, initialState);

  useEffect(() => {
    if (authInitialized && !authLoading) {
      // `resolvedUid` doubles as the "already looked this uid up" guard: without
      // it the lookup ran twice for users with no account, because settling it
      // flips `initialized` (a dependency) while `user` stays null.
      const needsLookup =
        !state.user && firebaseUser && !firebaseUser?.isAnonymous && state.resolvedUid !== firebaseUser.uid;

      if (needsLookup) {
        dispatch(setLoading(true));

        const fetchUserData = async () => {
          try {
            const user = await getUser(firebaseUser.uid, (await FirebaseAuthentication.getIdToken()).token);

            if (user) {
              dispatch(setUser(user));
            }

            dispatch(isInitialized());
            dispatch(setResolvedUid(firebaseUser.uid));
            dispatch(setLoading(false));

            if (user) await identifyUser(firebaseUser, user);
          } catch (e) {
            dispatch(isInitialized());
            // A failed lookup still resolves the uid — same outcome as "no
            // account", and leaving it unresolved would strand the UI.
            dispatch(setResolvedUid(firebaseUser.uid));
            dispatch(setLoading(false));
            captureException(e);
          }
        };

        fetchUserData();
      } else {
        // We already hold a record for this signed-in user and skipped the
        // lookup — most often an anonymous account that has just been linked to
        // a phone/email, which is a new non-anonymous uid for the same record.
        if (firebaseUser && !firebaseUser.isAnonymous && state.user && state.resolvedUid !== firebaseUser.uid) {
          dispatch(setResolvedUid(firebaseUser.uid));
        }

        if (!state.initialized) {
          dispatch(isInitialized());
        }
      }
    }
    // `user`/`resolvedUid` are read above, so they belong here: the branch that
    // marks an already-held record as resolved must re-run when `setUser` lands
    // from elsewhere (e.g. the create-user flow), not rely on an auth event
    // happening to fire alongside it.
  }, [state.initialized, state.user, state.resolvedUid, firebaseUser, authInitialized, authLoading]);

  return (
    <UserContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};

/**
 * Whether `user` reflects the currently signed-in Firebase user, i.e. the backend
 * lookup for this uid has settled. Route guards must gate on this rather than on
 * `initialized`/`!loading`: those are already true from the signed-out pass, so
 * for one commit after sign-in "signed in with no account" is indistinguishable
 * from "we haven't fetched yet" — which is what bounced onboarded users through
 * /onboarding. Derived during render, so it doesn't depend on effect ordering
 * (child effects, e.g. a route guard's <Navigate>, run before this provider's).
 *
 * Anonymous and signed-out users are always resolved — the signup funnel never waits.
 */
export const useUserResolved = (): boolean => {
  const { firebaseUser } = useAuthContext();
  const { resolvedUid } = useUserContext();

  if (!firebaseUser || firebaseUser.isAnonymous) return true;

  return resolvedUid === firebaseUser.uid;
};

export const useUserDispatch = () => {
  return useContext(UserDispatchContext);
};

/**
 * Refetches the user record on demand — the provider's own lookup fires once per uid, so
 * this is the only way to pick up records the backend created out-of-band. Never throws;
 * a failed refresh reports false and leaves the last-known user in place.
 */
export const useRefreshUser = () => {
  const { firebaseUser } = useAuthContext();
  const dispatch = useUserDispatch();

  return async (): Promise<boolean> => {
    if (!firebaseUser || firebaseUser.isAnonymous) return false;

    try {
      const user = await getUser(firebaseUser.uid, (await FirebaseAuthentication.getIdToken()).token);
      if (!user) return false;

      dispatch(setUser(user));
      return true;
    } catch (e) {
      captureException(e);
      return false;
    }
  };
};
